import { type DotLottie, DotLottieReact } from "@lottiefiles/dotlottie-react"
import { useTheme } from "components/theme/ThemeProvider"
import { type FC, useEffect, useMemo, useState } from "react"
import { LOTTIE_URL_PREFIX } from "src/components/images/consts"

export type LottieModes = "forward" | "reverse" | "bounce" | "reverse-bounce"

// New lotties need to be added here
export type LottieFileNameMapping = "po-logo" | "live-strategy" | "refresh"

export type LottieProps = {
  filename: LottieFileNameMapping
  color?: "primary" | "secondary" | "error" | "warning" | "positive"
  autoPlay?: boolean
  loop?: boolean
  speed?: number
  mode?: LottieModes
  className?: string
}

export const DEFAULT_LOTTIE_LOOP = true
export const DEFAULT_LOTTIE_SPEED = 1
export const DEFAULT_LOTTIE_MODE = "forward"
export const DEFAULT_LOTTIE_COLOR = "primary"

const Lottie: FC<LottieProps> = ({
  filename,
  loop = DEFAULT_LOTTIE_LOOP,
  speed = DEFAULT_LOTTIE_SPEED,
  mode = DEFAULT_LOTTIE_MODE,
  color = DEFAULT_LOTTIE_COLOR,
  autoPlay,
  className,
}) => {
  const { theme } = useTheme()
  const [dotLottie, setDotLottie] = useState<DotLottie | null>(null)
  const dotLottieRefCallback = (dotLottie: DotLottie) => {
    setDotLottie(dotLottie)
  }
  const initialAutoPlay = useMemo(() => autoPlay ?? true, [])

  const themeFolder = useMemo(() => {
    return ["light", "system-light"].includes(theme) ? "light" : "dark"
  }, [theme])

  const lottieSrc = useMemo(() => {
    return `${LOTTIE_URL_PREFIX}/${themeFolder}/${filename}.lottie`
  }, [themeFolder, filename])

  useEffect(() => {
    if (typeof autoPlay === "undefined" || !dotLottie) {
      return
    }

    if (autoPlay) {
      dotLottie.play()
    } else {
      dotLottie.stop()
    }
  }, [autoPlay, dotLottie])

  return (
    <div className={className}>
      <DotLottieReact
        src={lottieSrc}
        loop={loop}
        autoplay={initialAutoPlay}
        speed={speed}
        mode={mode}
        renderConfig={{ autoResize: true }}
        dotLottieRefCallback={dotLottieRefCallback}
      />
    </div>
  )
}

export default Lottie
